import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Trophy, Crown } from 'lucide-react';
import { getTokuLeaderboard } from '@/lib/api';
import { FloorTheme, getTheme } from '@/lib/themes';
import { Avatar } from '@/components/Avatar';
import { getMutedTextColor, getPanelStyle, getSoftSurface, isDarkTheme, pickTextColor, withAlpha } from '@/lib/themeStyles';

type PeriodKey = 'daily' | 'weekly' | 'monthly' | 'total';

interface LeaderboardEntry {
    rank: number;
    user_id: number;
    username: string;
    display_name?: string | null;
    avatar_path?: string | null;
    role_type: string;
    floor_level: number;
    score: number;
}

interface LeaderboardResponse {
    period: PeriodKey;
    generated_at: string;
    top: LeaderboardEntry[];
    me: {
        user_id: number;
        score: number;
        rank: number;
        total_users: number;
    };
}

const PERIOD_LABELS: Record<PeriodKey, string> = {
    daily: 'デイリー',
    weekly: 'ウィークリー',
    monthly: 'マンスリー',
    total: 'トータル',
};

export function TokuRanking() {
    const { theme, user } = useOutletContext<{ theme: FloorTheme; user: any }>();
    const [period, setPeriod] = useState<PeriodKey>('daily');
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<LeaderboardResponse | null>(null);
    const darkTheme = isDarkTheme(theme);
    const mutedTextColor = getMutedTextColor(theme);
    const panelStyle = getPanelStyle(theme, 0.96, 0.9);

    useEffect(() => {
        if (!user) return;
        const fetchData = async () => {
            setLoading(true);
            try {
                const res = await getTokuLeaderboard(period);
                setData(res.data);
            } catch (err) {
                console.error(err);
                setData(null);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [period, user]);

    const rows = useMemo(() => data?.top || [], [data]);

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            <div
                className="p-6 rounded-lg text-center"
                style={panelStyle}
            >
                <div className="flex items-center justify-center gap-2">
                    <Trophy size={20} />
                    <h1 className="text-3xl font-serif font-bold" style={{ color: theme.colors.primary }}>
                        Tokuランキング
                    </h1>
                </div>
                <p className="opacity-80 mt-2">
                    獲得したTokuポイントのランキングです。
                </p>
            </div>

            <div className="flex flex-wrap gap-3 justify-center">
                {(['daily', 'weekly', 'monthly', 'total'] as PeriodKey[]).map((key) => (
                    <button
                        key={key}
                        className="px-4 py-2 rounded-full text-sm font-semibold border transition"
                        onClick={() => setPeriod(key)}
                        style={{
                            borderColor: period === key ? theme.colors.primary : theme.colors.border,
                            backgroundColor: period === key ? theme.colors.primary : getSoftSurface(theme, 0.8, 0.44),
                            color: period === key ? pickTextColor(theme.colors.primary) : theme.colors.text,
                        }}
                    >
                        {PERIOD_LABELS[key]}
                    </button>
                ))}
            </div>

            <div
                className="rounded-lg p-4 border flex flex-col gap-2"
                style={panelStyle}
            >
                <div className="text-sm font-semibold">自分のランキング</div>
                <div className="flex flex-wrap items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                        <Crown size={16} />
                        <span>順位: {data?.me?.rank ?? '-'}</span>
                    </div>
                    <div>ポイント: {data?.me?.score ?? '-'}</div>
                    <div>参加人数: {data?.me?.total_users ?? '-'}</div>
                </div>
            </div>

            <div
                className="rounded-lg border overflow-hidden"
                style={{ borderColor: theme.colors.border }}
            >
                <div
                    className="px-4 py-3 text-sm font-semibold flex justify-between"
                    style={{
                        backgroundColor: getSoftSurface(theme, 0.84, 0.4),
                        color: theme.colors.text,
                    }}
                >
                    <span>Top 50</span>
                    {loading && <span style={{ color: mutedTextColor }}>読み込み中...</span>}
                </div>
                <div className="divide-y" style={{ borderColor: theme.colors.border }}>
                    {rows.length === 0 && !loading && (
                        <div className="p-6 text-center text-sm" style={{ color: mutedTextColor }}>ランキングデータがありません。</div>
                    )}
                    {rows.map((entry) => {
                        const isMe = entry.user_id === user?.user_id;
                        const avatarTheme = getTheme(entry.role_type, entry.floor_level);
                        return (
                            <div
                                key={`${entry.user_id}-${entry.rank}`}
                                className="flex items-center gap-4 px-4 py-3"
                                style={{
                                    backgroundColor: isMe
                                        ? withAlpha(theme.colors.primary, darkTheme ? 0.16 : 0.09)
                                        : withAlpha(theme.colors.card, darkTheme ? 0.88 : 0.82),
                                    color: theme.colors.text,
                                }}
                            >
                                <div className="w-8 text-sm font-bold" style={{ color: mutedTextColor }}>#{entry.rank}</div>
                                <Avatar
                                    src={entry.avatar_path || undefined}
                                    username={entry.display_name?.trim() || '住人'}
                                    size="sm"
                                    theme={avatarTheme}
                                />
                                <div className="flex-1">
                                    <div className="text-sm font-semibold" style={{ color: theme.colors.text }}>
                                        {entry.display_name?.trim() || '住人'}
                                    </div>
                                    <div className="text-xs" style={{ color: mutedTextColor }}>
                                        Floor {entry.floor_level} ・ {entry.role_type}
                                    </div>
                                </div>
                                <div className="text-sm font-semibold" style={{ color: theme.colors.primary }}>{entry.score}</div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
